import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input, Select } from '../components/form';
import { Alert, Button, Card, EmptyState, Spinner } from '../components/ui';
import { brl, mesAno, parseReais, reaisParaCampo } from '../lib/format';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
interface ItemPrevisao {
  id: number;
  categoria: string;
  item: string;
  valor: number;
}
interface Previsao {
  itens: ItemPrevisao[];
  subtotal: number;
  reserva: number;
  total: number;
  pctFundoReserva: number;
}

type Carga = { estado: 'carregando' } | { estado: 'erro' } | { estado: 'ok'; previsao: Previsao };
type Campo = 'categoria' | 'item' | 'valor';
type Aviso = { kind: 'success' | 'danger'; texto: string };

const MSG_CATEGORIA = 'Informe a categoria.';
const MSG_ITEM = 'Informe a descrição.';
const MSG_VALOR_FORMATO = 'Informe um valor em reais, como 1.234,56.';

/** Erros de validação da API que pertencem a um campo do formulário. */
const ERRO_DE_CAMPO: Record<string, { campo: Campo; mensagem: string }> = {
  invalid_item: { campo: 'categoria', mensagem: 'Informe a categoria e a descrição.' },
  invalid_valor: {
    campo: 'valor',
    mensagem: 'O valor deve ficar entre R$ 0,00 e R$ 10.000.000,00.',
  },
};

/** Demais erros da API (código → mensagem), mostrados acima da tabela. */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  not_found: 'Item não encontrado. A lista foi atualizada.',
  competencia_not_found: 'Competência não encontrada. Recarregue a página.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível concluir. Tente novamente.';

/**
 * Previsão orçamentária da competência (RF-A06). `editavel` só para o síndico de
 * condomínio ativo: POST/PATCH/DELETE são `requireRole('sindico')`, o contábil é
 * somente leitura e o tenant suspenso também (#127). Subtotal, reserva e total vêm
 * sempre do servidor (`@econdominio/calc`): a tela recarrega depois de cada alteração.
 */
export function Previsao({ editavel }: { editavel: boolean }) {
  const [comps, setComps] = useState<Competencia[] | null>(null);
  const [sel, setSel] = useState<string>('');
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  // Só os ids: o item em edição vem sempre da lista atual.
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [confirmandoId, setConfirmandoId] = useState<number | null>(null);
  const [excluindoId, setExcluindoId] = useState<number | null>(null);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  // Competência selecionada no momento: respostas de outra (troca rápida) são descartadas.
  const selAtual = useRef(sel);
  selAtual.current = sel;

  useEffect(() => {
    void getJson<{ competencias: Competencia[] }>('/api/competencias').then((d) => {
      const cs = d?.competencias ?? [];
      setComps(cs);
      if (cs[0]) setSel(String(cs[0].id));
    });
  }, []);

  const carregar = useCallback(async (competencia: string) => {
    const previsao = await getJson<Previsao>(
      `/api/competencias/${encodeURIComponent(competencia)}/previsao`,
    );
    if (selAtual.current !== competencia) return;
    setCarga(previsao ? { estado: 'ok', previsao } : { estado: 'erro' });
  }, []);

  useEffect(() => {
    if (!sel) return;
    setCarga({ estado: 'carregando' });
    setEditandoId(null);
    setConfirmandoId(null);
    void carregar(sel);
  }, [sel, carregar]);

  /** Erro da API fora dos campos; item inexistente fecha edição/confirmação e recarrega. */
  async function tratarFalha(codigo?: string) {
    setAviso({ kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO });
    if (codigo !== 'not_found') return;
    setEditandoId(null);
    setConfirmandoId(null);
    await carregar(sel);
  }

  async function aoSalvar(texto: string) {
    setAviso({ kind: 'success', texto });
    setEditandoId(null);
    await carregar(sel);
  }

  async function excluir(id: number) {
    // Um DELETE por vez: o segundo clique responderia 404 e esconderia o sucesso.
    if (excluindoId !== null) return;
    setExcluindoId(id);
    setAviso(null);
    const res = await mutateJson<{ error?: string }>(
      `/api/previsao-itens/${encodeURIComponent(id)}`,
      {},
      'DELETE',
    );
    setExcluindoId(null);
    setConfirmandoId(null);
    if (!res.ok) {
      await tratarFalha(res.data?.error);
      return;
    }
    setAviso({ kind: 'success', texto: 'Item excluído.' });
    await carregar(sel);
  }

  if (comps === null) return <Spinner />;
  if (comps.length === 0) return <EmptyState>Nenhuma competência aberta.</EmptyState>;

  const previsao = carga.estado === 'ok' ? carga.previsao : null;
  const emEdicao = previsao?.itens.find((i) => i.id === editandoId) ?? null;
  return (
    <div className={styles.stack}>
      <Field label="Competência">
        {(id) => (
          <Select
            id={id}
            value={sel}
            onChange={(e) => {
              setAviso(null);
              setSel(e.target.value);
            }}
            style={{ maxWidth: '12rem' }}
          >
            {comps.map((c) => (
              <option key={c.id} value={c.id}>
                {mesAno(c)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}
      {carga.estado === 'carregando' ? <Spinner /> : null}
      {carga.estado === 'erro' ? (
        <Alert kind="danger">Não foi possível carregar a previsão.</Alert>
      ) : null}

      {editavel && previsao ? (
        <ItemForm
          key={emEdicao?.id ?? 'novo'}
          competencia={sel}
          item={emEdicao}
          onSalvo={aoSalvar}
          onCancelar={() => setEditandoId(null)}
          onErroGeral={tratarFalha}
        />
      ) : null}

      {previsao ? (
        previsao.itens.length === 0 ? (
          <EmptyState>Sem itens nesta competência.</EmptyState>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Item</th>
                  <th className={styles.num}>Valor</th>
                  {editavel ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {previsao.itens.map((it) => (
                  <tr key={it.id}>
                    <td>{it.categoria}</td>
                    <td>{it.item}</td>
                    <td className={styles.num}>{brl(it.valor)}</td>
                    {editavel ? (
                      <td>
                        {confirmandoId === it.id ? (
                          <div className={styles.row}>
                            <span>Excluir este item?</span>
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={excluindoId !== null}
                              onClick={() => void excluir(it.id)}
                            >
                              Confirmar exclusão
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label="Cancelar exclusão"
                              onClick={() => setConfirmandoId(null)}
                            >
                              Cancelar
                            </Button>
                          </div>
                        ) : (
                          <div className={styles.row}>
                            <Button
                              size="sm"
                              variant="secondary"
                              aria-label={`Editar item ${it.item}`}
                              onClick={() => {
                                setAviso(null);
                                setConfirmandoId(null);
                                setEditandoId(it.id);
                              }}
                            >
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`Excluir item ${it.item}`}
                              onClick={() => {
                                setAviso(null);
                                setConfirmandoId(it.id);
                              }}
                            >
                              Excluir
                            </Button>
                          </div>
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={2}>
                    Subtotal
                  </th>
                  <td className={styles.num}>{brl(previsao.subtotal)}</td>
                  {editavel ? <td /> : null}
                </tr>
                <tr>
                  <th scope="row" colSpan={2}>
                    Fundo de reserva ({previsao.pctFundoReserva}%)
                  </th>
                  <td className={styles.num}>{brl(previsao.reserva)}</td>
                  {editavel ? <td /> : null}
                </tr>
                <tr className={styles.totalRow}>
                  <th scope="row" colSpan={2}>
                    Total
                  </th>
                  <td className={styles.num}>{brl(previsao.total)}</td>
                  {editavel ? <td /> : null}
                </tr>
              </tfoot>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
}

/** Inclusão (`item` nulo) ou edição; erros de validação vão para o campo. */
function ItemForm({
  competencia,
  item,
  onSalvo,
  onCancelar,
  onErroGeral,
}: {
  competencia: string;
  item: ItemPrevisao | null;
  onSalvo: (texto: string) => Promise<void>;
  onCancelar: () => void;
  /** Erro da API fora dos campos (código; ausente = falha de rede). */
  onErroGeral: (codigo?: string) => Promise<void>;
}) {
  const [categoria, setCategoria] = useState(item?.categoria ?? '');
  const [descricao, setDescricao] = useState(item?.item ?? '');
  const [valor, setValor] = useState(item ? reaisParaCampo(item.valor) : '');
  const [erros, setErros] = useState<Partial<Record<Campo, string>>>({});
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    const centavos = parseReais(valor);
    const locais: Partial<Record<Campo, string>> = {};
    if (!categoria.trim()) locais.categoria = MSG_CATEGORIA;
    if (!descricao.trim()) locais.item = MSG_ITEM;
    if (centavos === null) locais.valor = MSG_VALOR_FORMATO;
    setErros(locais);
    if (centavos === null || locais.categoria || locais.item) return;

    setEnviando(true);
    const corpo = { categoria: categoria.trim(), item: descricao.trim(), valor: centavos };
    const res = item
      ? await mutateJson<{ error?: string }>(
          `/api/previsao-itens/${encodeURIComponent(item.id)}`,
          corpo,
          'PATCH',
        )
      : await mutateJson<{ error?: string }>(
          `/api/competencias/${encodeURIComponent(competencia)}/previsao`,
          corpo,
        );
    setEnviando(false);

    if (res.ok) {
      if (!item) {
        setCategoria('');
        setDescricao('');
        setValor('');
      }
      await onSalvo(item ? 'Item salvo.' : 'Item adicionado.');
      return;
    }
    const codigo = res.data?.error;
    const deCampo = codigo ? ERRO_DE_CAMPO[codigo] : undefined;
    if (deCampo) setErros({ [deCampo.campo]: deCampo.mensagem });
    else await onErroGeral(codigo);
  }

  return (
    <Card title={item ? `Editar item ${item.item}` : 'Novo item'}>
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Categoria" error={erros.categoria}>
            {(id) => (
              <Input
                id={id}
                value={categoria}
                maxLength={80}
                onChange={(e) => setCategoria(e.target.value)}
              />
            )}
          </Field>
          <Field label="Descrição" error={erros.item}>
            {(id) => (
              <Input
                id={id}
                value={descricao}
                maxLength={120}
                onChange={(e) => setDescricao(e.target.value)}
              />
            )}
          </Field>
          <Field label="Valor" error={erros.valor}>
            {(id) => (
              <Input
                id={id}
                value={valor}
                inputMode="decimal"
                placeholder="0,00"
                onChange={(e) => setValor(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
        </div>
        <div className={styles.row}>
          <Button type="submit" disabled={enviando}>
            {item ? 'Salvar' : 'Adicionar'}
          </Button>
          {item ? (
            <Button variant="ghost" onClick={onCancelar}>
              Cancelar
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
